import { Exercise, Equipment, ExerciseFilters, EquipmentFilters } from '../types';
import { 
  DIFFICULTY_LEVELS, 
  RATING_LEVELS, 
  DANGER_LEVELS, 
  REP_RANGES,
  UTILIZATION_RANGES,
  COST_RANGES,
  AGE_RANGES
} from '../constants';

// Exercise filtering utilities
export const filterExercises = (exercises: Exercise[], filters: ExerciseFilters): Exercise[] => {
  return exercises.filter(exercise => {
    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      const matchesSearch = 
        exercise.name.toLowerCase().includes(searchLower) ||
        exercise.instructions.some(instruction => 
          instruction.toLowerCase().includes(searchLower)
        ) ||
        exercise.techniqueChecklist.some(check => 
          check.toLowerCase().includes(searchLower)
        );
      if (!matchesSearch) return false;
    }

    // Type filter
    if (filters.types.length > 0 && !filters.types.includes(exercise.type)) {
      return false;
    }

    // Difficulty range filter
    if (filters.difficultyRanges.length > 0) {
      const matchesDifficulty = filters.difficultyRanges.some(range => {
        const level = Object.values(DIFFICULTY_LEVELS).find(l => l.label === range);
        return level && exercise.difficulty >= level.min && exercise.difficulty <= level.max;
      });
      if (!matchesDifficulty) return false;
    }

    // Rating range filter
    if (filters.ratingRanges.length > 0) {
      const matchesRating = filters.ratingRanges.some(range => {
        const level = Object.values(RATING_LEVELS).find(l => l.label === range);
        return level && exercise.rating >= level.min && exercise.rating <= level.max;
      });
      if (!matchesRating) return false;
    }

    // Danger range filter
    if (filters.dangerRanges.length > 0) {
      const matchesDanger = filters.dangerRanges.some(range => {
        const level = Object.values(DANGER_LEVELS).find(l => l.label === range);
        return level && exercise.danger >= level.min && exercise.danger <= level.max;
      });
      if (!matchesDanger) return false;
    }

    // Rep range filter
    if (filters.repRanges.length > 0) {
      const matchesRepRange = filters.repRanges.some(range => {
        const repRange = Object.values(REP_RANGES).find(r => r.label === range);
        if (!repRange) return false;
        return exercise.repRange[0] >= repRange.min && exercise.repRange[1] <= repRange.max;
      });
      if (!matchesRepRange) return false;
    }

    // Symmetry mode filter
    if (filters.symmetryModes.length > 0 && !filters.symmetryModes.includes(exercise.symmetryMode)) {
      return false;
    }

    // Muscle groups filter
    if (filters.muscleGroups.length > 0) {
      const exerciseMuscleGroups = Object.keys(exercise.muscleActivations);
      const hasMatchingMuscleGroup = filters.muscleGroups.some(mg => 
        exerciseMuscleGroups.includes(mg)
      );
      if (!hasMatchingMuscleGroup) return false;
    }

    // Equipment filter
    if (filters.equipmentTypes.length > 0) {
      const exerciseEquipment = Object.keys(exercise.equipment);
      const hasMatchingEquipment = filters.equipmentTypes.some(eq => 
        exerciseEquipment.includes(eq)
      );
      if (!hasMatchingEquipment) return false;
    }

    return true;
  });
};

// Equipment filtering utilities
export const filterEquipment = (equipment: Equipment[], filters: EquipmentFilters): Equipment[] => {
  return equipment.filter(item => {
    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      const matchesSearch = 
        item.name.toLowerCase().includes(searchLower) ||
        item.brand.toLowerCase().includes(searchLower) ||
        item.model.toLowerCase().includes(searchLower) ||
        item.notes.toLowerCase().includes(searchLower) ||
        item.serialNumber.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Category filter
    if (filters.categories.length > 0 && !filters.categories.includes(item.category)) {
      return false;
    }

    // Status filter
    if (filters.statuses.length > 0 && !filters.statuses.includes(item.status)) {
      return false;
    }

    // Condition filter
    if (filters.conditions.length > 0 && !filters.conditions.includes(item.condition)) {
      return false;
    }

    // Brand filter
    if (filters.brands.length > 0 && !filters.brands.includes(item.brand)) {
      return false;
    }

    // Location filter
    if (filters.locations.length > 0 && !filters.locations.includes(item.location)) {
      return false;
    }

    // Maintenance schedule filter
    if (filters.maintenanceSchedules.length > 0 && !filters.maintenanceSchedules.includes(item.maintenanceSchedule)) {
      return false;
    }

    // Utilization range filter
    if (filters.utilizationRanges.length > 0) {
      const matchesUtilization = filters.utilizationRanges.some(range => {
        const level = Object.values(UTILIZATION_RANGES).find(l => l.label === range);
        return level && item.utilization >= level.min && item.utilization <= level.max;
      });
      if (!matchesUtilization) return false;
    }

    // Cost range filter
    if (filters.costRanges.length > 0) {
      const matchesCost = filters.costRanges.some(range => {
        const level = Object.values(COST_RANGES).find(l => l.label === range);
        return level && item.acquisitionCost >= level.min && item.acquisitionCost <= level.max;
      });
      if (!matchesCost) return false;
    }

    // Age range filter
    if (filters.ageRanges.length > 0) {
      const acquisitionDate = new Date(item.acquisitionDate);
      const ageInYears = (Date.now() - acquisitionDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
      
      const matchesAge = filters.ageRanges.some(range => {
        const level = Object.values(AGE_RANGES).find(l => l.label === range);
        return level && ageInYears >= level.min && ageInYears <= level.max;
      });
      if (!matchesAge) return false;
    }

    // Maintenance due filter
    if (filters.maintenanceDue) {
      const nextMaintenanceDate = new Date(item.nextMaintenance);
      const isOverdue = nextMaintenanceDate < new Date();
      if (!isOverdue) return false;
    }

    // Warranty expiring filter
    if (filters.warrantyExpiring && item.warrantyExpiry) {
      const warrantyDate = new Date(item.warrantyExpiry);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      const isExpiringSoon = warrantyDate <= thirtyDaysFromNow;
      if (!isExpiringSoon) return false;
    }

    return true;
  });
};

// Search utilities
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Filter option utilities
export const getUniqueValues = <T>(
  items: T[],
  accessor: (item: T) => string | string[]
): string[] => {
  const values = new Set<string>();
  items.forEach(item => {
    const value = accessor(item);
    if (Array.isArray(value)) {
      value.forEach(v => values.add(v));
    } else {
      values.add(value);
    }
  });
  return Array.from(values).sort();
};

export const getFilterOptions = (values: string[], counts?: { [key: string]: number }) => {
  return values.map(value => ({
    label: value,
    value,
    count: counts?.[value]
  }));
};

// Statistics utilities
export const calculateStats = (items: any[], totalPossible: number = 100) => {
  const coverage = (items.length / totalPossible) * 100;
  return {
    total: items.length,
    coverage: Math.min(coverage, 100)
  };
}; 